// components/billings/billings.component.jsx
import React, { useMemo, useState } from "react";

import { useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { FiPrinter } from "react-icons/fi";

import InvoicesList from "./invoices-list.component";
import ExpensesList from "./expenses-list.component";

import {
  PERIOD_OPTIONS,
  addCurrencyMaps,
  subtractCurrencyMaps,
  formatCurrencyTotals,
  getDateInputValue,
  getMonthInputValue,
  getPeriodRange,
} from "./billing.utils";

import "./billings.styles.scss";

export default function Billings({
  currentSpaceId: currentSpaceIdProp,
  display = "flex",
  onOpenInvoice,
  activeTab = "billings",
}) {
  const location = useLocation();

  const currentUser = useSelector((state) => state.user.currentUser);

  // Preserves your current route structure while also allowing
  // currentSpaceId to be passed directly later.
  const pathnameSpaceId = location.pathname.split("/")[2];

  const currentSpaceId = currentSpaceIdProp || pathnameSpaceId;

  const today = useMemo(() => new Date(), []);

  // const [activeTab, setActiveTab] = useState("billings");
  const [selectedPeriod, setSelectedPeriod] = useState("today");

  const [selectedDay, setSelectedDay] = useState(() =>
    getDateInputValue(today),
  );

  const [selectedMonth, setSelectedMonth] = useState(() =>
    getMonthInputValue(today),
  );

  const [selectedYear, setSelectedYear] = useState(String(today.getFullYear()));

  const [weekStartsOn, setWeekStartsOn] = useState("monday");

  const [paidRevenueByCurrency, setPaidRevenueByCurrency] = useState({});

  const [unpaidRevenueByCurrency, setUnpaidRevenueByCurrency] = useState({});

  const [paidExpensesByCurrency, setPaidExpensesByCurrency] = useState({});

  const [unpaidExpensesByCurrency, setUnpaidExpensesByCurrency] = useState({});

  const periodRange = useMemo(
    () =>
      getPeriodRange({
        periodKey: selectedPeriod,
        selectedDay,
        selectedMonth,
        selectedYear,
        weekStartsOn,
      }),
    [selectedPeriod, selectedDay, selectedMonth, selectedYear, weekStartsOn],
  );

  const actualProfitByCurrency = useMemo(
    () => subtractCurrencyMaps(paidRevenueByCurrency, paidExpensesByCurrency),
    [paidRevenueByCurrency, paidExpensesByCurrency],
  );

  const projectedProfitByCurrency = useMemo(() => {
    const totalRevenue = addCurrencyMaps(
      paidRevenueByCurrency,
      unpaidRevenueByCurrency,
    );

    const totalExpenses = addCurrencyMaps(
      paidExpensesByCurrency,
      unpaidExpensesByCurrency,
    );

    return subtractCurrencyMaps(totalRevenue, totalExpenses);
  }, [
    paidRevenueByCurrency,
    unpaidRevenueByCurrency,
    paidExpensesByCurrency,
    unpaidExpensesByCurrency,
  ]);

  // Navigate to Invoice Builder Component for New Invoice or Edit Invoice
  const navigate = useNavigate();

const openNewInvoice = (clientId = "", paid = false) => {
  const params = new URLSearchParams();

  if (clientId) params.set("clientId", clientId);
  if (paid) params.set("paid", "true");

  const query = params.toString();

  navigate(
    `/s/${currentSpaceId}/billings/invoices/new${query ? `?${query}` : ""}`,
    {
      state: {
        backgroundPath: location.pathname + location.search,
      },
    },
  );
};

  const openExistingInvoice = (invoice) => {
    navigate(`/s/${currentSpaceId}/billings/invoices/${invoice.id}`, {
      state: {
        backgroundPath: location.pathname + location.search,
      },
    });
  };

  const openNewExpense = () => {
    navigate(`/s/${currentSpaceId}/billings/expenses/new`, {
      state: {
        backgroundPath: location.pathname + location.search,
      },
    });
  };

  const openExistingExpense = (expense) => {
    if (!expense?.id) return;

    navigate(`/s/${currentSpaceId}/billings/expenses/${expense.id}`, {
      state: {
        backgroundPath: location.pathname + location.search,
      },
    });
  };

  return (
    <div
      id="billings"
      className="billings"
      style={{
        display,
        flexDirection: "column",
        height: "100%",
      }}
    >
      <div className="billings__panel">
        <div className="billings__toolbar">
          <div className="billings__period-controls">
            <select
              value={selectedPeriod}
              onChange={(event) => setSelectedPeriod(event.target.value)}
              aria-label="Billing period"
            >
              {PERIOD_OPTIONS.map((option) => (
                <option key={option.key} value={option.key}>
                  {option.label}
                </option>
              ))}
            </select>

            {selectedPeriod === "thisweek" && (
              <select
                value={weekStartsOn}
                onChange={(event) => setWeekStartsOn(event.target.value)}
                aria-label="First day of week"
              >
                <option value="monday">Week starts Monday</option>

                <option value="sunday">Week starts Sunday</option>
              </select>
            )}

            {selectedPeriod === "selectday" && (
              <input
                type="date"
                value={selectedDay}
                onChange={(event) => setSelectedDay(event.target.value)}
                aria-label="Selected day"
              />
            )}

            {selectedPeriod === "selectmonth" && (
              <input
                type="month"
                value={selectedMonth}
                onChange={(event) => setSelectedMonth(event.target.value)}
                aria-label="Selected month"
              />
            )}

            {selectedPeriod === "selectyear" && (
              <input
                type="number"
                min={today.getFullYear() - 10}
                max={today.getFullYear() + 2}
                value={selectedYear}
                onChange={(event) => setSelectedYear(event.target.value)}
                aria-label="Selected year"
              />
            )}
          </div>

          <button
            type="button"
            className="billings__report-button"
            onClick={() => window.alert("Exporting reports is coming soon.")}
          >
            <FiPrinter />
            Export
          </button>
        </div>

        <div className="billings__content">
          {/*
            Both components remain mounted. Therefore the profit
            summary includes expenses even while Billings is active.
          */}

          <div hidden={activeTab !== "billings"}>
            <InvoicesList
              spaceId={currentSpaceId}
              currentUserId={currentUser?.uid}
              allSpaceBillings
              filterByPeriod
              periodRange={periodRange}
              onOpenInvoice={openExistingInvoice}
              setPaidRevenueByCurrency={setPaidRevenueByCurrency}
              setUnpaidRevenueByCurrency={setUnpaidRevenueByCurrency}
              onAddInvoice={openNewInvoice}
            />
          </div>

          <div hidden={activeTab !== "expenses"}>
            <ExpensesList
              spaceId={currentSpaceId}
              currentUserId={currentUser?.uid}
              periodRange={periodRange}
              onOpenExpense={openExistingExpense}
              setPaidExpensesByCurrency={setPaidExpensesByCurrency}
              setUnpaidExpensesByCurrency={setUnpaidExpensesByCurrency}
              onAddExpense={openNewExpense}
            />
          </div>
        </div>

        <div className="billings__profit-summary">
          <span>
            Profit (actual):{" "}
            <strong>{formatCurrencyTotals(actualProfitByCurrency)}</strong>
          </span>

          <span>
            Profit (projected):{" "}
            <strong>{formatCurrencyTotals(projectedProfitByCurrency)}</strong>
          </span>
        </div>
      </div>
    </div>
  );
}
